// @ddd/crisis-support: the fleet's one crisis-support table.
//
// Apps import this file by RELATIVE path from their own src/…/crisisSupport.js
// (not through a Vite alias), because the Knowledge Hub importer loads those
// files in plain Node to index them for the coaches, and Node cannot see a
// Vite alias. See README.md.
import { COUNTRIES, SERVICES, DIRECTORIES } from './services.js';
import { LISTS } from './lists.js';
import { RETIRED } from './retired.js';
import { COACH_LISTS, COACH_ROLES } from './coach.js';

export { COUNTRIES, SERVICES, DIRECTORIES, LISTS, RETIRED, COACH_LISTS, COACH_ROLES };
export { parseTextLine, isPhone, textLineSmsHref, coachLineDetail, coachCardLines } from './textLines.js';

const deepFreeze = (value) => {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const inner of Object.values(value)) deepFreeze(inner);
  }
  return value;
};

// A service as the apps have always received it, plus its provenance. `id`
// is kept so a surface can ask for one service by name (serviceById).
const toEntry = (id) => {
  const { country, ...fields } = SERVICES[id];
  return { ...fields, id };
};

const buildTable = (list) => {
  const table = {};
  for (const [key, ids] of Object.entries(list)) {
    const { name, flag, emergency } = COUNTRIES[key];
    table[key] = { name, flag, emergency: { ...emergency }, services: ids.map(toEntry) };
  }
  return deepFreeze(table);
};

// Built once, so every surface in an app shares the same object.
const TABLES = Object.fromEntries(Object.entries(LISTS).map(([audience, list]) => [audience, buildTable(list)]));

/**
 * The crisis table for an audience ('adult' or 'young'), keyed by country:
 *   { australia: { name, flag, emergency: { name, number, description },
 *                  services: [{ name, number, description, website, type,
 *                               youthFocused?, verifiedOn, source, id }] } }
 * Throws on an unknown audience: that is a typo in an app, and it should
 * fail the build rather than ship an empty table.
 */
export const crisisSupportTable = (audience) => {
  const table = TABLES[audience];
  if (!table) throw new Error(`@ddd/crisis-support: unknown audience "${audience}" (expected ${Object.keys(TABLES).join(' or ')})`);
  return table;
};

/** One service by id, in the same shape as a table entry. Throws on an unknown id. */
export const serviceById = (id) => {
  if (!SERVICES[id]) throw new Error(`@ddd/crisis-support: unknown service "${id}"`);
  return deepFreeze(toEntry(id));
};

export const COUNTRY_KEYS = Object.keys(COUNTRIES);

// Australia is the region we *offer* by default, never a silent fallback:
// every surface that shows it labels it and offers the other six.
export const DEFAULT_COUNTRY = 'australia';

// Two-letter codes (old saved preferences, the coach's consent gate, the
// Knowledge Hub) map onto the table's keys. UK and GB both mean the UK.
const CODE_TO_KEY = { UK: 'uk', GB: 'uk' };
for (const [key, { code }] of Object.entries(COUNTRIES)) CODE_TO_KEY[code] = key;

// Browser language tags carry ISO regions, where the UK is GB, never UK.
const REGION_TO_KEY = { ...CODE_TO_KEY };
delete REGION_TO_KEY.UK;

/** A table key from a key, a two-letter code or a loose name ('New Zealand'); null when it is none of the seven. */
export const normaliseCountryKey = (value) => {
  if (!value) return null;
  if (COUNTRIES[value]) return value;
  const upper = String(value).toUpperCase();
  if (CODE_TO_KEY[upper]) return CODE_TO_KEY[upper];
  const flat = String(value).toLowerCase().replace(/[^a-z]/g, '');
  return COUNTRIES[flat] ? flat : null;
};

/** The two-letter code for a country (UK for the United Kingdom); null when unknown. */
export const countryCodeFor = (value) => COUNTRIES[normaliseCountryKey(value)]?.code || null;

/**
 * Region from the browser's language tag (en-AU, en-IN…); null when it is
 * not one of the seven, so the caller shows a picker rather than a guess.
 */
export const detectCountry = () => {
  try {
    const tags = [navigator.language, ...(navigator.languages || [])].filter(Boolean);
    for (const tag of tags) {
      const region = String(tag).split('-')[1];
      if (region && REGION_TO_KEY[region.toUpperCase()]) return REGION_TO_KEY[region.toUpperCase()];
    }
  } catch { /* no navigator */ }
  return null;
};

/**
 * The reader's remembered country, under an app's own storage key.
 * `store: 'code'` saves the two-letter code instead of the table key, for an
 * app whose other code reads the same key that way.
 *   const { readCountryPref, writeCountryPref, initialCountry } =
 *     countryPreference('myapp_crisis_country');
 */
export const countryPreference = (storageKey, { store = 'key' } = {}) => {
  const readCountryPref = () => {
    try {
      return normaliseCountryKey(localStorage.getItem(storageKey));
    } catch {
      return null;
    }
  };
  const writeCountryPref = (value) => {
    try {
      const key = normaliseCountryKey(value);
      if (key) localStorage.setItem(storageKey, store === 'code' ? COUNTRIES[key].code : key);
    } catch { /* storage unavailable */ }
  };
  // Stored choice, then browser region, then the worked example.
  const initialCountry = () => readCountryPref() || detectCountry() || DEFAULT_COUNTRY;
  return { readCountryPref, writeCountryPref, initialCountry };
};

/**
 * Advice for when we hold nothing for the reader's region. All prose about
 * emergency contact should come through here, so it can never name a number
 * we do not hold.
 */
export const emergencyCallAdvice = () =>
  "If you're in immediate danger, call your local emergency number now. If you're not sure what it is, your phone's emergency dialler lists it.";
